<script lang="ts">
import type { BadgeSlots, BadgeProps as UBadgeProps } from '#ui/types';
import { tv } from 'tailwind-variants';
import { getColorInfo } from '../utils/colors';

export interface BadgeProps extends Omit<UBadgeProps, 'color' | 'variant'> {
	color?: UBadgeProps['color'] | 'custom';
	variant?: UBadgeProps['variant'] | 'outline-neutral';
	customColor?: string;
	rounded?: boolean;
}

const badge = tv({
	variants: {
		rounded: {
			true: 'rounded-full',
		},
	},
});
</script>

<script setup lang="ts">
import { reactiveOmit } from '@vueuse/core';
import { computed } from 'vue';

import Badge from '#ui/components/Badge.vue';

const props = defineProps<BadgeProps>();
const slots = defineSlots<BadgeSlots>();

const forwarded = reactiveOmit(props, ['customColor', 'rounded']);

const badgeProps = computed(() => {
	return {
		...forwarded,
		color: props.color as UBadgeProps['color'],
		variant: props.variant as UBadgeProps['variant'],
		class: badge({ rounded: props.rounded, class: props.class }),
	};
});

const badgeStyle = computed(() => {
	const { color, customColor, variant } = props;

	if (color === 'custom' && customColor) {
		const { isLight, isDark } = getColorInfo(customColor);

		const baseStyle = {
			'--badge-background': customColor,
		};

		if (variant === 'soft' || variant === 'subtle') {
			return {
				...baseStyle,
				'--badge-text': 'var(--ui-text-highlighted)',
				'--badge-background': customColor,
			};
		}
		else if (variant === 'outline') {
			return {
				...baseStyle,
				'--badge-text': 'var(--ui-text-highlighted)',
				'--badge-ring': isLight ? 'var(--ui-border-highlighted)' : (isDark ? `light-dark(${customColor}, var(--ui-text))` : customColor),
			};
		}
		else {
			return {
				...baseStyle,
				'--badge-text': isLight
					? 'light-dark(var(--ui-text), var(--ui-text-inverted))'
					: 'light-dark(var(--ui-text-inverted), var(--ui-text))',
			};
		}
	}

	return {};
});
</script>

<template>
	<Badge
		v-bind="badgeProps"
		:style="badgeStyle"
	>
		<template v-for="(_, name) in slots" #[name]="slotData">
			<slot :name v-bind="slotData" />
		</template>
	</Badge>
</template>
